import React, { FC } from "react";
import { Box } from "@mui/material";
import { useIntl } from "react-intl";

import { useAlarmPaletteResolver } from "../../../../../../../../../hooks/use-alarm-color";
import { IStationTrainDefectIcon } from "../interfaces";
import { STATION_DEFECT_ICON_SRCS } from "../station-defect-icon-assets";
import { buildStationTrainDefectIconRows } from "./station-train-defect-icon-rows";

export interface IStationTrainDefectIconsProps {
  icons: Array<IStationTrainDefectIcon | null>;
  cellHeight?: number;
  cellWidth?: number;
}

const CELL_BACKGROUND = "#D9D9D9";

export const StationTrainDefectIcons: FC<IStationTrainDefectIconsProps> = ({ icons, cellHeight = 25, cellWidth }) => {
  const { formatMessage } = useIntl();
  const resolveAlarmColor = useAlarmPaletteResolver();

  const displayIconRows = buildStationTrainDefectIconRows(icons);
  const columnCount = Math.max(...displayIconRows.map(row => row.length));

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${columnCount}, ${cellWidth === undefined ? "minmax(0, 1fr)" : `${cellWidth}px`})`,
        gridTemplateRows: `repeat(${displayIconRows.length}, ${cellHeight}px)`,
        gap: "2px",
        width: cellWidth === undefined ? "100%" : columnCount * cellWidth + (columnCount - 1) * 2,
        height: displayIconRows.length * cellHeight + (displayIconRows.length - 1) * 2,
        flexShrink: 0,
      }}
    >
      {displayIconRows.flatMap((row, rowIndex) =>
        row.map((icon, columnIndex) => {
          const label = icon
            ? `${formatMessage({ id: `pages.scheme.stationTile.defectIcons.${icon.type}` })}: ${formatMessage({
                id: `enums.alarms.${icon.alarmStatus}`,
                defaultMessage: String(icon.alarmStatus),
              })}`
            : undefined;

          return (
            <Box
              key={`${rowIndex}-${columnIndex}`}
              aria-label={label}
              aria-hidden={icon ? undefined : true}
              role={icon ? "img" : undefined}
              title={label}
              sx={{
                gridRow: rowIndex + 1,
                gridColumn: columnIndex + 1,
                backgroundColor: icon ? CELL_BACKGROUND : undefined,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
                height: cellHeight,
                visibility: icon ? "visible" : "hidden",
                border: "1px solid #8E8E8E",
                borderRadius: "3px",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              {icon ? (
                <Box
                  component="img"
                  src={STATION_DEFECT_ICON_SRCS[icon.type]}
                  alt=""
                  aria-hidden="true"
                  sx={{
                    display: "block",
                    width: "auto",
                    height: "auto",
                    maxWidth: "88%",
                    maxHeight: "88%",
                    objectFit: "contain",
                    backgroundColor: resolveAlarmColor(icon.alarmStatus),
                    pointerEvents: "none",
                  }}
                />
              ) : null}
            </Box>
          );
        }),
      )}
    </Box>
  );
};
